import { useState, useEffect, useCallback, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import AnalyticsTab from './AnalyticsTab';

const API = process.env.REACT_APP_API_URL || "https://web-production-7510.up.railway.app";
const req = async (path, opts={}) => {
  const r = await fetch(`${API}${path}`, { headers:{"Content-Type":"application/json"}, ...opts });
  if(!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
  return r.json();
};

const api = {
  getSettings:   ()     => req("/api/settings"),
  getWatchlist:  ()     => req("/api/watchlist"),
  addStock:      (s)    => req("/api/watchlist", {method:"POST",body:JSON.stringify({symbol:s})}),
  removeStock:   (s)    => req(`/api/watchlist/${s}`, {method:"DELETE"}),
  getQuote:      (s)    => req(`/api/quote/${s}`),
  getSignals:    ()     => req("/api/signals/latest"),
  generateAll:   ()     => req("/api/signals/generate-all", {method:"POST"}),
  scannerStart:  ()     => req("/api/scanner/start", {method:"POST"}),
  scannerStatus: (id)   => req(`/api/scanner/status/${id}`),
  executeTrade:  (body) => req("/api/trades/execute", {method:"POST",body:JSON.stringify(body)}),
  manualExit:    (id)   => req(`/api/trades/${id}/manual-exit`, {method:"POST"}),
  getOpenTrades: ()     => req("/api/trades/open"),
  getHistory:    ()     => req("/api/trades/history?limit=50"),
  getPortfolio:  ()     => req("/api/portfolio/summary"),
  getDailyPnL:   ()     => req("/api/pnl/daily"),
  getLoginUrl:   ()     => req("/api/auth/login-url"),
  logout:        ()     => req("/api/auth/logout", {method:"POST"}),
  switchMode:    (mode) => req("/api/settings/mode", {method:"POST",body:JSON.stringify({mode})}),
};

const C = {
  bg:"#f0f4f8", panel:"#ffffff", panel2:"#f8fafc", border:"#e2e8f0", border2:"#cbd5e1",
  text:"#1e293b", muted:"#64748b", dim:"#94a3b8",
  amber:"#d97706", amberBg:"#fffbeb", amberBrd:"#fde68a",
  green:"#16a34a", greenBg:"#f0fdf4", greenBrd:"#bbf7d0",
  red:"#dc2626",   redBg:"#fef2f2",   redBrd:"#fecaca",
  teal:"#0369a1",  tealBg:"#eff6ff",  tealBrd:"#bfdbfe",
  purple:"#7c3aed",purpleBg:"#f5f3ff",purpleBrd:"#ddd6fe",
};

const css=`
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{background:${C.bg};color:${C.text};font-family:'Plus Jakarta Sans',sans-serif;}
  ::-webkit-scrollbar{width:3px;height:3px;}
  ::-webkit-scrollbar-thumb{background:${C.border2};border-radius:4px;}
  .btn{cursor:pointer;border:none;outline:none;transition:all 0.15s;font-family:inherit;}
  .btn:hover{filter:brightness(0.93);}
  .btn:active{transform:scale(0.97);}
  .pulse{animation:pulse 2s infinite;}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
  .fade{animation:fade 0.2s ease;}
  @keyframes fade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
  .trow:hover{background:#f8fafc!important;}
  .spin{animation:spin 1s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg)}}
  /* Mobile nav */
  .mob-nav{display:none;}
  @media(max-width:768px){
    .mob-nav{display:flex;}
    .desk-only{display:none!important;}
    .mob-full{width:100%!important;height:auto!important;overflow:visible!important;}
    .mob-stack{flex-direction:column!important;}
    .mob-pad{padding:10px!important;}
    .mob-hide{display:none!important;}
  }
  @media(min-width:769px){
    .mob-only{display:none!important;}
  }
`;

const isMobile = () => window.innerWidth <= 768;

const P=({children,style})=>(<div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)",...style}}>{children}</div>);
const PH=({title,right,color=C.amber})=>(<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 14px",borderBottom:`1px solid ${C.border}`,background:C.panel2,borderRadius:"12px 12px 0 0"}}><span style={{fontSize:10,letterSpacing:2,color,fontWeight:700}}>{title}</span>{right}</div>);
const Pill=({label,c,bg,bd})=>(<span style={{fontSize:9,fontWeight:700,color:c,background:bg,padding:"3px 8px",borderRadius:20,border:`1px solid ${bd}`,letterSpacing:0.5,whiteSpace:"nowrap"}}>{label}</span>);
const DirBadge=({d})=>{
  const m={BUY:{c:C.green,bg:C.greenBg,bd:C.greenBrd},SELL:{c:C.red,bg:C.redBg,bd:C.redBrd},HOLD:{c:C.amber,bg:C.amberBg,bd:C.amberBrd}};
  const s=m[d]||m.HOLD; return <Pill label={d} c={s.c} bg={s.bg} bd={s.bd}/>;
};
const ConfBar=({v})=>{
  const pct=Math.round((v||0)*100),col=pct>65?C.green:pct>45?C.amber:C.red;
  return(<div style={{display:"flex",alignItems:"center",gap:8}}><div style={{flex:1,height:5,background:C.border,borderRadius:3,overflow:"hidden"}}><div style={{width:`${pct}%`,height:"100%",background:col,borderRadius:3}}/></div><span style={{fontSize:10,fontWeight:700,color:col,minWidth:28}}>{pct}%</span></div>);
};
const Empty=({icon,title,sub})=>(<div style={{padding:"32px 16px",textAlign:"center"}}><div style={{fontSize:24,marginBottom:8}}>{icon}</div><div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:4}}>{title}</div>{sub&&<div style={{fontSize:10,color:C.dim,lineHeight:1.6}}>{sub}</div>}</div>);
const Spin=()=>(<div style={{display:"flex",justifyContent:"center",padding:24}}><div className="spin" style={{width:20,height:20,border:`2px solid ${C.border}`,borderTopColor:C.amber,borderRadius:"50%"}}/></div>);

// ── Signal Card ───────────────────────────────────────────────────
function SignalCard({sig,onExecute,executing,mode}){
  const [exp,setExp]=useState(false);
  const [qty,setQty]=useState(sig.suggested_qty||Math.max(1,Math.floor(50000/(sig.entry||sig.cmp||1))));
  const dc=sig.direction==="BUY"?C.green:sig.direction==="SELL"?C.red:C.amber;
  const doExec=()=>onExecute({signal_id:sig.id||sig.signal_id||null,symbol:sig.symbol,direction:sig.direction,quantity:qty,entry_price:sig.entry||sig.cmp,target1:sig.target1,target2:sig.target2,stop_loss:sig.stop_loss});
  return(
    <div className="fade" style={{background:C.panel,border:`1px solid ${C.border}`,borderLeft:`4px solid ${dc}`,borderRadius:12,padding:14,marginBottom:10,boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:6}}>
          <span style={{fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:800,color:C.text}}>{sig.symbol}</span>
          <DirBadge d={sig.direction}/>
          {sig.timeframe&&sig.timeframe!=="N/A"&&<Pill label={sig.timeframe.replace(/_/g," ")} c={C.teal} bg={C.tealBg} bd={C.tealBrd}/>}
          {sig.scanner_score&&<Pill label={`⭐${sig.scanner_score}`} c={sig.scanner_score>=70?C.green:C.amber} bg={sig.scanner_score>=70?C.greenBg:C.amberBg} bd={sig.scanner_score>=70?C.greenBrd:C.amberBrd}/>}
        </div>
        <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
          <div style={{fontSize:8,color:C.muted}}>CMP</div>
          <div style={{fontSize:13,fontWeight:700,color:C.text}}>₹{sig.cmp||"—"}</div>
        </div>
      </div>
      {sig.scanner_reasons&&sig.scanner_reasons.length>0&&(
        <div style={{background:C.purpleBg,border:`1px solid ${C.purpleBrd}`,borderRadius:7,padding:"6px 10px",marginBottom:10,fontSize:9,color:C.purple,lineHeight:1.5}}>
          🔍 {sig.scanner_reasons.slice(0,2).join(" · ")}
        </div>
      )}
      {sig.direction!=="HOLD"&&sig.entry>0&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:6,marginBottom:10}}>
          {[{l:"ENTRY",v:sig.entry,c:C.amber,bg:C.amberBg,bd:C.amberBrd},
            {l:"STOP LOSS",v:sig.stop_loss,c:C.red,bg:C.redBg,bd:C.redBrd},
            {l:"TARGET 1",v:sig.target1,c:C.green,bg:C.greenBg,bd:C.greenBrd},
            {l:"TARGET 2",v:sig.target2,c:C.green,bg:C.greenBg,bd:C.greenBrd}].map(x=>(
            <div key={x.l} style={{background:x.bg,borderRadius:7,padding:"8px 10px",border:`1px solid ${x.bd}`}}>
              <div style={{fontSize:8,color:x.c,marginBottom:3,letterSpacing:1,fontWeight:700,opacity:0.75}}>{x.l}</div>
              <div style={{fontSize:14,fontWeight:700,color:x.c}}>₹{x.v||"—"}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{marginBottom:10}}>
        <div style={{fontSize:9,fontWeight:700,color:C.muted,marginBottom:5}}>AI CONFIDENCE</div>
        <ConfBar v={sig.confidence}/>
      </div>
      {sig.indicators_checked&&sig.indicators_checked.length>0&&(
        <div style={{marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:9,fontWeight:700,color:C.muted}}>
              {sig.direction==="HOLD"?"WHY HOLD — INDICATOR BREAKDOWN":"INDICATOR CHECKLIST"}
            </span>
            <span style={{fontSize:9,fontWeight:700,
              color:sig.indicators_passed>=7?C.green:sig.indicators_passed>=5?C.amber:C.red}}>
              {sig.indicators_passed}/{sig.indicators_total} passed
            </span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
            {sig.indicators_checked.map(c=>(
              <div key={c.name} style={{display:"flex",alignItems:"flex-start",gap:5,
                background:c.passed?C.greenBg:C.panel2,
                border:`1px solid ${c.passed?C.greenBrd:C.border}`,
                borderRadius:6,padding:"5px 7px"}}>
                <span style={{fontSize:10,lineHeight:1,flexShrink:0,marginTop:1}}>
                  {c.passed?"✅":"❌"}
                </span>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:9,fontWeight:700,
                    color:c.passed?C.green:C.muted,
                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {c.name}
                  </div>
                  <div style={{fontSize:8,color:C.dim,lineHeight:1.3,
                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {c.note}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {sig.key_risks&&(
        <div style={{background:C.redBg,border:`1px solid ${C.redBrd}`,borderRadius:6,
          padding:"5px 9px",marginBottom:10,fontSize:9,color:C.red,lineHeight:1.5}}>
          ⚠️ {sig.key_risks}
        </div>
      )}
      <div style={{marginTop:10}}>
        {sig.direction!=="HOLD"&&sig.entry>0?(
          <div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,background:C.panel2,borderRadius:7,padding:"7px 10px",border:`1px solid ${C.border}`}}>
              <span style={{fontSize:10,color:C.muted,fontWeight:600,whiteSpace:"nowrap"}}>Qty:</span>
              <input type="number" min="1" value={qty} onChange={e=>setQty(Math.max(1,parseInt(e.target.value)||1))}
                style={{width:60,background:C.panel,border:`1px solid ${C.border2}`,borderRadius:5,padding:"3px 7px",fontSize:11,fontWeight:700,color:C.text,fontFamily:"inherit",outline:"none",textAlign:"center"}}/>
              <span style={{fontSize:10,color:C.amber,fontWeight:700}}>= ₹{Math.round(qty*(sig.entry||0)).toLocaleString("en-IN")}</span>
            </div>
            <button className="btn" onClick={doExec} disabled={executing===sig.id}
              style={{width:"100%",padding:"13px",borderRadius:8,fontSize:12,fontWeight:700,fontFamily:"inherit",background:executing===sig.id?C.border:dc,color:executing===sig.id?C.muted:"#fff",border:"none",boxShadow:executing===sig.id?"none":`0 2px 8px ${dc}44`}}>
              {executing===sig.id?"⏳ Placing...":`⚡ Execute ${sig.direction} — ${sig.symbol}`}
            </button>
          </div>
        ):(
          <div style={{textAlign:"center",padding:"10px",fontSize:10,fontWeight:600,color:C.amber,background:C.amberBg,borderRadius:7,border:`1px solid ${C.amberBrd}`}}>
            🟡 HOLD — No clear setup today
          </div>
        )}
      </div>
    </div>
  );
}

// ── Scanner Panel ─────────────────────────────────────────────────
function ScannerPanel({onExecute,executing,mode,authenticated}){
  const [jobId,   setJobId]   = useState(null);
  const [job,     setJob]     = useState(null);
  const [results, setResults] = useState([]);
  const [scored,  setScored]  = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [msg,     setMsg]     = useState(null);
  const pollRef = useRef(null);

  const toast_=(m,c=C.green)=>{ setMsg({m,c}); setTimeout(()=>setMsg(null),5000); };

  const startScan=async()=>{
    if(!authenticated){ toast_("Login to Zerodha first",C.red); return; }
    try {
      const res=await api.scannerStart();
      setJobId(res.job_id);
      setJob({status:"running",phase:"scoring",progress:0,total:res.total_stocks,progress_ai:0,total_ai:0,current_stock:""});
      setResults([]); setScored([]);
      toast_("🔍 Scan started in background — no timeout!",C.purple);
    } catch(e){ toast_("Failed: "+e.message,C.red); }
  };

  useEffect(()=>{
    if(!jobId) return;
    pollRef.current=setInterval(async()=>{
      try {
        const s=await api.scannerStatus(jobId);
        setJob(s);
        if(s.status==="done"){
          clearInterval(pollRef.current);
          setResults(s.results||[]);
          setScored(s.all_scored||[]);
          setLastRun(new Date().toLocaleTimeString("en-IN",{hour12:true}));
          const b=(s.results||[]).filter(r=>r.direction==="BUY").length;
          const sl=(s.results||[]).filter(r=>r.direction==="SELL").length;
          toast_(`✅ Done — ${b} BUY · ${sl} SELL from ${s.scanned} stocks`);
        }
      } catch(e){ clearInterval(pollRef.current); }
    },5000);
    return()=>clearInterval(pollRef.current);
  },[jobId]);

  const isRunning=job&&job.status==="running";
  const p1=job?Math.round((job.progress/Math.max(job.total,1))*100):0;
  const p2=job&&job.total_ai>0?Math.round((job.progress_ai/job.total_ai)*100):0;

  return(
    <div>
      {msg&&<div style={{background:C.panel,border:`1px solid ${msg.c}33`,borderLeft:`4px solid ${msg.c}`,borderRadius:9,padding:"10px 14px",fontSize:11,fontWeight:600,color:C.text,marginBottom:12}}>{msg.m}</div>}
      <div style={{display:"flex",flexDirection:"column",gap:12,padding:"14px",background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:6,marginBottom:4}}>
              <span style={{fontSize:13,fontWeight:700,color:C.text}}>🔍 Auto Discovery</span>
              <Pill label="50 NSE Stocks" c={C.purple} bg={C.purpleBg} bd={C.purpleBrd}/>
            </div>
            <div style={{fontSize:10,color:C.muted,lineHeight:1.5}}>
              {isRunning?`${job.phase==="scoring"?"Phase 1: Scoring stocks":"Phase 2: AI signals"} — runs in background`:
               lastRun?`Last: ${lastRun} · ${results.length} signals`:"Finds best setups from 50 NSE stocks automatically"}
            </div>
          </div>
          <button className="btn" onClick={startScan} disabled={isRunning}
            style={{background:isRunning?C.border:C.purple,color:isRunning?C.muted:"#fff",fontFamily:"inherit",fontWeight:700,fontSize:11,padding:"11px 18px",borderRadius:9,border:"none",whiteSpace:"nowrap",minWidth:130,boxShadow:isRunning?"none":"0 2px 8px rgba(124,58,237,0.35)"}}>
            {isRunning?"⏳ Scanning...":"🔍 Run Discovery"}
          </button>
        </div>
        {isRunning&&(
          <div>
            <div style={{marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:C.muted,marginBottom:4}}>
                <span>Phase 1: Scoring {job.progress}/{job.total} stocks {job.current_stock&&`(${job.current_stock})`}</span>
                <span>{p1}%</span>
              </div>
              <div style={{height:7,background:C.border,borderRadius:4,overflow:"hidden"}}>
                <div style={{width:`${p1}%`,height:"100%",background:p1>=100?C.green:C.purple,borderRadius:4,transition:"width 0.5s"}}/>
              </div>
            </div>
            {job.total_ai>0&&(
              <div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:C.muted,marginBottom:4}}>
                  <span>Phase 2: AI signals {job.progress_ai}/{job.total_ai}</span>
                  <span>{p2}%</span>
                </div>
                <div style={{height:7,background:C.border,borderRadius:4,overflow:"hidden"}}>
                  <div style={{width:`${p2}%`,height:"100%",background:C.amber,borderRadius:4,transition:"width 0.5s"}}/>
                </div>
              </div>
            )}
            <div style={{marginTop:8,fontSize:9,color:C.dim}}>💡 Switch tabs freely — results appear automatically when done.</div>
          </div>
        )}
      </div>

      {results.length>0&&(
        <>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <span style={{fontSize:10,fontWeight:700,color:C.muted}}>TOP PICKS · {results.filter(r=>r.direction!=="HOLD").length} ACTIONABLE</span>
            {scored.length>0&&<button className="btn" onClick={()=>setShowAll(s=>!s)} style={{fontSize:9,color:C.teal,background:C.tealBg,border:`1px solid ${C.tealBrd}`,padding:"3px 10px",borderRadius:5,fontFamily:"inherit",fontWeight:600}}>{showAll?"Hide":"All Scores"}</button>}
          </div>
          {[...results]
            .sort((a,b)=>{
              // Actionable first, then by confidence descending
              if(a.direction!=="HOLD"&&b.direction==="HOLD") return -1;
              if(a.direction==="HOLD"&&b.direction!=="HOLD") return 1;
              return (b.confidence||0)-(a.confidence||0);
            })
            .map(sig=><SignalCard key={sig.symbol} sig={sig} onExecute={onExecute} executing={executing} mode={mode}/>)}
        </>
      )}

      {showAll&&scored.length>0&&(
        <P style={{marginTop:10}}>
          <PH title={`ALL SCORED (${scored.length})`} color={C.purple}/>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead><tr style={{background:C.panel2,borderBottom:`1px solid ${C.border}`}}>
                {["#","Symbol","Score","CMP","RSI","Vol","Reason"].map(h=><th key={h} style={{padding:"8px 8px",textAlign:"left",color:C.muted,fontWeight:700,fontSize:9,whiteSpace:"nowrap"}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {scored.map((s,i)=>(
                  <tr key={s.symbol} className="trow" style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"8px",color:i<3?C.amber:C.dim,fontWeight:700,fontSize:10}}>#{i+1}</td>
                    <td style={{padding:"8px",fontWeight:700,color:C.text}}>{s.symbol}</td>
                    <td style={{padding:"8px"}}>
                      <span style={{fontSize:10,fontWeight:700,color:s.score>=70?C.green:s.score>=50?C.amber:C.red}}>{s.score}</span>
                    </td>
                    <td style={{padding:"8px",fontWeight:600,fontSize:10}}>₹{s.cmp}</td>
                    <td style={{padding:"8px",color:s.rsi<40?C.green:s.rsi>65?C.red:C.muted,fontWeight:600,fontSize:10}}>{s.rsi}</td>
                    <td style={{padding:"8px",color:s.vol_ratio>=1.5?C.green:C.muted,fontSize:10}}>{s.vol_ratio}x</td>
                    <td style={{padding:"8px",fontSize:9,color:C.dim,maxWidth:140}}>{s.reasons?.[0]||"—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </P>
      )}

      {!isRunning&&results.length===0&&(
        <Empty icon="🔍" title="No scan results yet" sub={"Tap Run Discovery to automatically scan 50 NSE stocks.\nResults appear here — you can switch tabs while it runs."}/>
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────
export default function App(){
  const [mode,          setMode]          = useState("paper");
  const [watchlist,     setWatchlist]     = useState([]);
  const [quotes,        setQuotes]        = useState({});
  const [signals,       setSignals]       = useState([]);
  const [watchlistSignals, setWatchlistSignals] = useState([]); // FIX: separate from scanner results
  const [openTrades,    setOpenTrades]    = useState([]);
  const [history,       setHistory]       = useState([]);
  const [portfolio,     setPortfolio]     = useState(null);
  const [dailyPnL,      setDailyPnL]      = useState([]);
  const [selected,      setSelected]      = useState("ALL");
  const [activeTab,     setActiveTab]     = useState("scanner");
  const [scanning,      setScanning]      = useState(false);
  const [executing,     setExecuting]     = useState(null);
  const [toast,         setToast]         = useState(null);
  const [newSym,        setNewSym]        = useState("");
  const [loading,       setLoading]       = useState(true);
  const [time,          setTime]          = useState("");
  const [mktOpen,       setMktOpen]       = useState(false);
  const [loginUrl,      setLoginUrl]      = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [liveQuotes,    setLiveQuotes]    = useState({});
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [mobile,        setMobile]        = useState(isMobile());
  const qt  = useRef(null);
  const pos = useRef(null);

  useEffect(()=>{
    const onResize=()=>setMobile(isMobile());
    window.addEventListener("resize",onResize);
    return()=>window.removeEventListener("resize",onResize);
  },[]);

  useEffect(()=>{
    const tick=()=>{
      const ist=new Date(new Date().toLocaleString("en-US",{timeZone:"Asia/Kolkata"}));
      const h=ist.getHours(),m=ist.getMinutes();
      setMktOpen((h>9||(h===9&&m>=15))&&(h<15||(h===15&&m<=30)));
      setTime(ist.toLocaleTimeString("en-IN",{hour12:true,hour:"numeric",minute:"2-digit"})+" IST");
    };
    tick(); const id=setInterval(tick,1000); return()=>clearInterval(id);
  },[]);

  const toast_=useCallback((msg,c=C.green)=>{ setToast({msg,c}); setTimeout(()=>setToast(null),3500); },[]);

  const loadAll=useCallback(async()=>{
    setLoading(true);
    const [wl,sigs,open,hist,port,pnl,settings,login]=await Promise.allSettled([
      api.getWatchlist(),api.getSignals(),api.getOpenTrades(),api.getHistory(),
      api.getPortfolio(),api.getDailyPnL(),api.getSettings(),api.getLoginUrl(),
    ]);
    if(wl.status==="fulfilled")       setWatchlist(wl.value||[]);
    if(sigs.status==="fulfilled")     setSignals(sigs.value||[]);
    if(open.status==="fulfilled")     setOpenTrades(open.value||[]);
    if(hist.status==="fulfilled")     setHistory(hist.value||[]);
    if(port.status==="fulfilled")     setPortfolio(port.value);
    // FIX: backend returns total_pnl not gross_pnl
    if(pnl.status==="fulfilled")      setDailyPnL((pnl.value||[]).slice(0,10).reverse().map(d=>({date:d.date?.slice(5)||d.date,pnl:d.total_pnl||d.gross_pnl||0})));
    if(settings.status==="fulfilled") { setMode(settings.value?.mode||"paper"); setAuthenticated(!!settings.value?.authenticated); }
    if(login.status==="fulfilled")    { setLoginUrl(login.value?.login_url||""); if(login.value?.authenticated) setAuthenticated(true); }
    setLoading(false);
  },[]);

  useEffect(()=>{ loadAll(); },[loadAll]);

  useEffect(()=>{
    const fetch_=async()=>{
      if(!mktOpen||!authenticated||!watchlist.length) return;
      const res=await Promise.allSettled(watchlist.map(w=>api.getQuote(w.symbol).then(q=>({symbol:w.symbol,...q}))));
      const map={}; res.forEach(r=>{ if(r.status==="fulfilled") map[r.value.symbol]=r.value; }); setQuotes(map);
    };
    fetch_(); qt.current=setInterval(fetch_,60000); return()=>clearInterval(qt.current);
  },[mktOpen,authenticated,watchlist]);

  useEffect(()=>{
    const refresh=async()=>{
      const [open,port]=await Promise.allSettled([api.getOpenTrades(),api.getPortfolio()]);
      if(open.status==="fulfilled") setOpenTrades(open.value||[]);
      if(port.status==="fulfilled") setPortfolio(port.value);
      if(open.status==="fulfilled"&&open.value?.length>0&&authenticated){
        const syms=[...new Set(open.value.map(t=>t.symbol))];
        const qr=await Promise.allSettled(syms.map(s=>api.getQuote(s).then(q=>({symbol:s,...q}))));
        const map={}; qr.forEach(r=>{ if(r.status==="fulfilled") map[r.value.symbol]=r.value; }); setLiveQuotes(map);
      }
    };
    if(activeTab==="positions"){ refresh(); pos.current=setInterval(refresh,30000); }
    return()=>clearInterval(pos.current);
  },[activeTab,authenticated]);

  const handleScan=async()=>{
    if(!authenticated){ toast_("Login to Zerodha first",C.red); return; }
    setScanning(true);
    try {
      toast_("⏳ Scanning watchlist...",C.amber);
      const res=await api.generateAll();
      const sigs=res.signals||[];
      // FIX: write to watchlistSignals only — never pollute shared signals with scanner results
      setWatchlistSignals(sigs);
      toast_(`✅ ${sigs.length} signals generated`);
    } catch(e){ toast_("Scan failed",C.red); }
    setScanning(false);
  };

  const handleExecute=async(body)=>{
    setExecuting(body.signal_id);
    try {
      await api.executeTrade(body);
      const [open,port]=await Promise.all([api.getOpenTrades(),api.getPortfolio()]);
      setOpenTrades(open||[]); setPortfolio(port);
      toast_(`✅ ${body.direction} — ${body.symbol} @ ₹${body.entry_price}`);
    } catch(e){ toast_("Execute failed: "+e.message,C.red); }
    setExecuting(null);
  };

  const handleExit=async(id)=>{
    try {
      const res=await api.manualExit(id);
      const [open,hist,port]=await Promise.all([api.getOpenTrades(),api.getHistory(),api.getPortfolio()]);
      setOpenTrades(open||[]); setHistory(hist||[]); setPortfolio(port);
      toast_(`Exited — P&L: ${(res.pnl||0)>=0?"+":""}₹${Math.round(Math.abs(res.pnl||0)).toLocaleString("en-IN")}`,(res.pnl||0)>=0?C.green:C.red);
    } catch(e){ toast_("Exit failed",C.red); }
  };

  const handleAdd=async()=>{
    if(!newSym.trim()||watchlist.length>=10) return;
    try { await api.addStock(newSym.toUpperCase()); const wl=await api.getWatchlist(); setWatchlist(wl||[]); toast_(`Added ${newSym}`); setNewSym(""); }
    catch(e){ toast_("Failed: "+e.message,C.red); }
  };

  const handleRemove=async(s)=>{
    try { await api.removeStock(s); const wl=await api.getWatchlist(); setWatchlist(wl||[]); toast_(`Removed ${s}`,C.red); }
    catch(e){ toast_("Failed",C.red); }
  };

  const handleMode=async(m)=>{
    if(m==="live"&&!window.confirm("⚠️ LIVE mode places REAL orders!\n\nSwitch to Live?")) return;
    try { await api.switchMode(m); setMode(m); toast_(`Switched to ${m.toUpperCase()}`,m==="live"?C.red:C.green); }
    catch(e){ toast_("Failed",C.red); }
  };

  const handleLogout=async()=>{
    try { await api.logout(); setAuthenticated(false); setQuotes({}); toast_("Logged out",C.amber); setTimeout(()=>loadAll(),1500); }
    catch(e){ toast_("Logout failed",C.red); }
  };

  const enriched=watchlist.map(w=>{ const q=quotes[w.symbol]; return {...w,ltp:q?.ltp||null,chg:q?.change_pct||null}; });
  // FIX: use watchlistSignals — never show scanner results in the Watchlist tab
  const filtered=selected==="ALL"?watchlistSignals:watchlistSignals.filter(s=>s.symbol===selected);
  const wr=portfolio?.win_rate_pct||0,pf=portfolio?.profit_factor||0,tot=portfolio?.trades_total||0;

  if(loading) return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:C.bg,flexDirection:"column",gap:14}}>
      <style>{css}</style>
      <div className="spin" style={{width:32,height:32,border:`3px solid ${C.border}`,borderTopColor:C.amber,borderRadius:"50%"}}/>
      <div style={{fontSize:12,fontWeight:600,color:C.muted}}>Connecting...</div>
    </div>
  );

  const tabs=[
    {k:"scanner",  l:"🔍", label:"Discover",   n:null},
    {k:"watchlist",l:"📋", label:"Watchlist",   n:watchlistSignals.length},
    {k:"positions",l:"📂", label:"Positions",   n:openTrades.length},
    {k:"history",  l:"📜", label:"History",     n:history.length},
    {k:"portfolio",l:"💰", label:"Portfolio",   n:null},
    {k:"analytics",l:"📊", label:"Analytics",   n:null},
  ];

  return(
    <div style={{display:"flex",flexDirection:"column",minHeight:"100vh",background:C.bg}}>
      <style>{css}</style>

      {/* Toast */}
      {toast&&<div style={{position:"fixed",top:mobile?10:64,right:mobile?8:16,left:mobile?8:"auto",zIndex:9999,background:C.panel,border:`1px solid ${toast.c}33`,borderLeft:`4px solid ${toast.c}`,borderRadius:10,padding:"11px 14px",fontSize:11,fontWeight:600,color:C.text,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",animation:"fade 0.2s ease"}}>{toast.msg}</div>}

      {/* HEADER */}
      <div style={{background:C.panel,borderBottom:`1px solid ${C.border}`,padding:mobile?"0 12px":"0 20px",display:"flex",alignItems:"center",justifyContent:"space-between",height:mobile?50:58,flexShrink:0,position:"sticky",top:0,zIndex:100,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:30,height:30,borderRadius:8,background:C.amber,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:"#fff",fontFamily:"'Syne',sans-serif"}}>A</div>
          {!mobile&&<div>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:14,color:C.text}}>AI Trading Assistant</div>
            <div style={{fontSize:8,color:C.dim}}>Claude AI · Zerodha Kite Connect</div>
          </div>}
          {mobile&&<div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:13,color:C.text}}>AI Trader</div>}
        </div>

        <div style={{display:"flex",alignItems:"center",gap:mobile?8:12}}>
          {/* Market status */}
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <div className={mktOpen?"pulse":""} style={{width:7,height:7,borderRadius:"50%",background:mktOpen?C.green:C.dim}}/>
            {!mobile&&<span style={{fontSize:10,fontWeight:700,color:mktOpen?C.green:C.muted}}>{mktOpen?"Open":"Closed"}</span>}
          </div>

          {/* P&L — mobile compact */}
          {portfolio&&(
            <div style={{background:C.panel2,borderRadius:7,padding:mobile?"4px 8px":"6px 12px",border:`1px solid ${C.border}`}}>
              <div style={{fontSize:mobile?11:13,fontWeight:800,color:(portfolio.total_pnl||0)>=0?C.green:C.red}}>
                {(portfolio.total_pnl||0)>=0?"+":""}₹{Math.round(portfolio.total_pnl||0).toLocaleString("en-IN")}
              </div>
            </div>
          )}

          {/* Auth status */}
          <div style={{display:"flex",alignItems:"center",gap:5,background:authenticated?C.greenBg:C.redBg,border:`1px solid ${authenticated?C.greenBrd:C.redBrd}`,borderRadius:7,padding:mobile?"4px 7px":"5px 10px"}}>
            <span style={{fontSize:mobile?9:9,fontWeight:700,color:authenticated?C.green:C.red}}>{authenticated?`✓${mobile?"":" Connected"}`:"✗ Login"}</span>
          </div>

          {/* Login / Logout */}
          {!authenticated&&loginUrl&&(
            <a href={loginUrl} target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
              <button className="btn" style={{background:C.teal,color:"#fff",border:"none",padding:mobile?"6px 10px":"7px 12px",borderRadius:7,fontSize:10,fontWeight:700,fontFamily:"inherit"}}>
                🔑 {mobile?"Login":"Login to Zerodha"}
              </button>
            </a>
          )}
          {authenticated&&!mobile&&(
            <>
              {loginUrl&&<a href={loginUrl} target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
                <button className="btn" style={{background:C.panel2,color:C.muted,border:`1px solid ${C.border}`,padding:"6px 10px",borderRadius:7,fontSize:10,fontWeight:700,fontFamily:"inherit"}}>🔑 Re-Login</button>
              </a>}
              <button className="btn" onClick={handleLogout} style={{background:C.redBg,color:C.red,border:`1px solid ${C.redBrd}`,padding:"6px 10px",borderRadius:7,fontSize:10,fontWeight:700,fontFamily:"inherit"}}>⏻ Logout</button>
            </>
          )}

          {/* Mode toggle — desktop only */}
          {!mobile&&<div style={{display:"flex",gap:2,background:C.panel2,borderRadius:7,padding:3,border:`1px solid ${C.border}`}}>
            {["paper","live"].map(m=>(
              <button key={m} className="btn" onClick={()=>handleMode(m)} style={{padding:"4px 10px",borderRadius:5,fontSize:9,fontWeight:700,letterSpacing:0.5,fontFamily:"inherit",background:mode===m?(m==="live"?C.red:C.amber):"transparent",color:mode===m?"#fff":C.muted}}>
                {m.toUpperCase()}
              </button>
            ))}
          </div>}

          <span style={{fontSize:mobile?9:10,color:C.muted,display:mobile?"none":"block"}}>{time}</span>
          <button className="btn" onClick={loadAll} style={{background:C.panel2,color:C.muted,border:`1px solid ${C.border}`,padding:"5px 8px",borderRadius:6,fontSize:13}}>↻</button>
        </div>
      </div>

      {/* Mode banner */}
      <div style={{padding:"4px 16px",fontSize:9,fontWeight:700,textAlign:"center",background:mode==="live"?C.redBg:C.amberBg,borderBottom:`1px solid ${mode==="live"?C.redBrd:C.amberBrd}`,color:mode==="live"?C.red:C.amber}}>
        {mode==="paper"?"📄 Paper Trading — Simulated, no real money":"⚡ LIVE MODE — Real orders on Zerodha"}
      </div>

      {/* BODY */}
      <div style={{display:"flex",flex:1,overflow:mobile?"visible":"hidden",paddingBottom:mobile?70:0}}>

        {/* WATCHLIST — desktop sidebar / mobile drawer */}
        {!mobile&&(
          <div style={{width:196,flexShrink:0,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column",background:C.panel,overflow:"hidden"}}>
            <div style={{padding:"11px 14px",borderBottom:`1px solid ${C.border}`,background:C.panel2,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:10,letterSpacing:2,color:C.amber,fontWeight:700}}>WATCHLIST</span>
              <span style={{fontSize:10,fontWeight:700,color:C.muted,background:C.border,padding:"2px 7px",borderRadius:10}}>{watchlist.length}/10</span>
            </div>
            <div style={{flex:1,overflowY:"auto"}}>
              {!enriched.length&&<div style={{padding:16,textAlign:"center",color:C.dim,fontSize:11,lineHeight:1.6}}>Add stocks below ↓</div>}
              {enriched.map(w=>(
                <div key={w.symbol} onClick={()=>setSelected(w.symbol)}
                  style={{padding:"10px 14px",borderBottom:`1px solid ${C.border}`,cursor:"pointer",borderLeft:selected===w.symbol?`4px solid ${C.amber}`:"4px solid transparent",background:selected===w.symbol?C.amberBg:C.panel}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:C.text,marginBottom:1}}>{w.symbol}</div>
                      <button className="btn" onClick={e=>{e.stopPropagation();handleRemove(w.symbol);}} style={{fontSize:8,color:C.red,background:"transparent",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit"}}>✕ remove</button>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:12,fontWeight:700,color:C.text}}>{w.ltp?`₹${w.ltp}`:"—"}</div>
                      {w.chg!=null&&<span style={{fontSize:9,fontWeight:700,color:w.chg>=0?C.green:C.red,background:w.chg>=0?C.greenBg:C.redBg,padding:"1px 5px",borderRadius:10,border:`1px solid ${w.chg>=0?C.greenBrd:C.redBrd}`}}>{w.chg>=0?"+":""}{w.chg?.toFixed(1)}%</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{padding:"9px 12px",borderTop:`1px solid ${C.border}`,background:C.panel2,display:"flex",gap:5}}>
              <input value={newSym} onChange={e=>setNewSym(e.target.value.toUpperCase())} onKeyDown={e=>e.key==="Enter"&&handleAdd()} placeholder="NSE symbol..." maxLength={12}
                style={{flex:1,background:C.panel,border:`1px solid ${C.border2}`,color:C.text,fontSize:10,fontWeight:600,padding:"6px 9px",borderRadius:6,fontFamily:"inherit",outline:"none"}}/>
              <button className="btn" onClick={handleAdd} style={{background:C.amber,color:"#fff",fontSize:15,fontWeight:700,padding:"6px 11px",borderRadius:6}}>+</button>
            </div>
          </div>
        )}

        {/* Mobile watchlist drawer */}
        {mobile&&showWatchlist&&(
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",zIndex:200}} onClick={()=>setShowWatchlist(false)}>
            <div style={{position:"absolute",top:0,left:0,bottom:0,width:"80%",maxWidth:300,background:C.panel,display:"flex",flexDirection:"column"}} onClick={e=>e.stopPropagation()}>
              <div style={{padding:"14px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:C.panel2}}>
                <span style={{fontSize:11,fontWeight:700,color:C.amber,letterSpacing:2}}>WATCHLIST</span>
                <button className="btn" onClick={()=>setShowWatchlist(false)} style={{background:C.border,color:C.muted,border:"none",borderRadius:6,padding:"4px 8px",fontSize:12}}>✕</button>
              </div>
              <div style={{flex:1,overflowY:"auto"}}>
                {!enriched.length&&<div style={{padding:16,textAlign:"center",color:C.dim,fontSize:11}}>Add stocks below</div>}
                {enriched.map(w=>(
                  <div key={w.symbol} onClick={()=>{ setSelected(w.symbol); setShowWatchlist(false); }}
                    style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,cursor:"pointer",borderLeft:selected===w.symbol?`4px solid ${C.amber}`:"4px solid transparent",background:selected===w.symbol?C.amberBg:C.panel}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:C.text}}>{w.symbol}</div>
                        <button className="btn" onClick={e=>{e.stopPropagation();handleRemove(w.symbol);}} style={{fontSize:9,color:C.red,background:"transparent",border:"none",padding:0,fontFamily:"inherit"}}>✕ remove</button>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:13,fontWeight:700}}>{w.ltp?`₹${w.ltp}`:"—"}</div>
                        {w.chg!=null&&<span style={{fontSize:10,fontWeight:700,color:w.chg>=0?C.green:C.red}}>{w.chg>=0?"+":""}{w.chg?.toFixed(1)}%</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{padding:"10px 12px",borderTop:`1px solid ${C.border}`,background:C.panel2,display:"flex",gap:6}}>
                <input value={newSym} onChange={e=>setNewSym(e.target.value.toUpperCase())} onKeyDown={e=>e.key==="Enter"&&handleAdd()} placeholder="NSE symbol..." maxLength={12}
                  style={{flex:1,background:C.panel,border:`1px solid ${C.border2}`,color:C.text,fontSize:11,fontWeight:600,padding:"8px 10px",borderRadius:7,fontFamily:"inherit",outline:"none"}}/>
                <button className="btn" onClick={handleAdd} style={{background:C.amber,color:"#fff",fontSize:16,fontWeight:700,padding:"8px 12px",borderRadius:7}}>+</button>
              </div>
            </div>
          </div>
        )}

        {/* MAIN CONTENT */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:mobile?"visible":"hidden"}}>

          {/* Desktop tabs */}
          {!mobile&&(
            <div style={{display:"flex",gap:6,padding:"10px 12px",background:C.bg,borderBottom:`1px solid ${C.border}`,flexWrap:"wrap"}}>
              {tabs.filter(t=>t.k!=="portfolio").map(t=>(
                <button key={t.k} className="btn" onClick={()=>setActiveTab(t.k)}
                  style={{padding:"7px 14px",borderRadius:8,fontSize:10,fontWeight:700,fontFamily:"inherit",display:"flex",alignItems:"center",gap:5,background:activeTab===t.k?C.amber:"transparent",color:activeTab===t.k?"#fff":C.muted,border:activeTab===t.k?"none":`1px solid ${C.border}`,boxShadow:activeTab===t.k?"0 2px 6px rgba(217,119,6,0.3)":"none"}}>
                  {t.l} {t.label}
                  {t.n!=null&&t.n>0&&<span style={{background:activeTab===t.k?"rgba(255,255,255,0.3)":C.border,color:activeTab===t.k?"#fff":C.muted,borderRadius:10,padding:"0 6px",fontSize:9,fontWeight:700}}>{t.n}</span>}
                </button>
              ))}
            </div>
          )}

          {/* Mobile: watchlist toggle bar */}
          {mobile&&(
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:C.panel,borderBottom:`1px solid ${C.border}`}}>
              <button className="btn" onClick={()=>setShowWatchlist(true)} style={{background:C.amberBg,color:C.amber,border:`1px solid ${C.amberBrd}`,padding:"6px 12px",borderRadius:7,fontSize:10,fontWeight:700,fontFamily:"inherit"}}>
                📋 Watchlist ({watchlist.length})
              </button>
              <div style={{flex:1,display:"flex",gap:1,background:C.panel2,borderRadius:7,padding:2,border:`1px solid ${C.border}`}}>
                {["paper","live"].map(m=>(
                  <button key={m} className="btn" onClick={()=>handleMode(m)} style={{flex:1,padding:"5px",borderRadius:5,fontSize:9,fontWeight:700,fontFamily:"inherit",background:mode===m?(m==="live"?C.red:C.amber):"transparent",color:mode===m?"#fff":C.muted}}>
                    {m.toUpperCase()}
                  </button>
                ))}
              </div>
              {authenticated&&loginUrl&&(
                <a href={loginUrl} target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
                  <button className="btn" style={{background:C.panel2,color:C.muted,border:`1px solid ${C.border}`,padding:"6px 8px",borderRadius:7,fontSize:9,fontWeight:700,fontFamily:"inherit"}}>🔑 Re-Login</button>
                </a>
              )}
              {authenticated&&<button className="btn" onClick={handleLogout} style={{background:C.redBg,color:C.red,border:`1px solid ${C.redBrd}`,padding:"6px 8px",borderRadius:7,fontSize:9,fontWeight:700,fontFamily:"inherit"}}>⏻</button>}
            </div>
          )}

          {/* ── CONTENT PANELS ─────────────────────────────────────────────
              KEY FIX: ScannerPanel is always mounted (never unmounts on tab switch).
              CSS display:none hides it — preserving jobId, results, polling state.
              All other tabs use normal conditional rendering (no polling state to preserve).
          ─────────────────────────────────────────────────────────────── */}
          <div style={{flex:1,overflowY:mobile?"visible":"auto",padding:mobile?"10px":"12px"}}>

            {/* SCANNER — always mounted, hidden via CSS to preserve scan state across tab switches */}
            <div style={{display:activeTab==="scanner"?"block":"none"}}>
              <ScannerPanel onExecute={handleExecute} executing={executing} mode={mode} authenticated={authenticated}/>
            </div>

            {/* WATCHLIST SCAN */}
            {activeTab==="watchlist"&&<div>
              <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:2}}>Watchlist Scan</div>
                  <div style={{fontSize:10,color:C.muted}}>{!authenticated?"Login first":watchlist.length===0?"Add stocks":mktOpen?"Market open":"Market closed — EOD data"}</div>
                </div>
                <button className="btn" onClick={handleScan} disabled={scanning||!authenticated||watchlist.length===0}
                  style={{background:scanning||!authenticated||watchlist.length===0?C.border:C.amber,color:scanning||!authenticated||watchlist.length===0?C.muted:"#fff",fontFamily:"inherit",fontWeight:700,fontSize:11,padding:"10px 16px",borderRadius:8,border:"none",whiteSpace:"nowrap",opacity:scanning||!authenticated||watchlist.length===0?0.6:1}}>
                  {scanning?"⏳ Scanning...":!authenticated?"🔒 Login":"⚡ Run Scan"}
                </button>
              </div>
              {filtered.length===0
                ?<Empty icon="📡" title="No signals yet" sub={authenticated?"Tap Run Scan to analyse your watchlist.":"Login to Zerodha first."}/>
                :filtered.map(sig=><SignalCard key={sig.id||sig.symbol} sig={sig} onExecute={handleExecute} executing={executing} mode={mode}/>)}
            </div>}

            {/* POSITIONS */}
            {activeTab==="positions"&&<div>
              <div style={{background:C.tealBg,border:`1px solid ${C.tealBrd}`,borderRadius:8,padding:"9px 12px",marginBottom:10,fontSize:10,color:C.teal,fontWeight:600,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>📡 Auto-updates every 5 mins · Auto-closes at SL/Target</span>
                <button className="btn" onClick={async()=>{ const [o,p]=await Promise.all([api.getOpenTrades(),api.getPortfolio()]); setOpenTrades(o||[]); setPortfolio(p); }} style={{background:C.teal,color:"#fff",border:"none",padding:"4px 10px",borderRadius:5,fontSize:10,fontWeight:700,fontFamily:"inherit",whiteSpace:"nowrap"}}>↻ Refresh</button>
              </div>
              <P>
                <PH title="OPEN POSITIONS" right={<Pill label={`${openTrades.length} Open`} c={C.green} bg={C.greenBg} bd={C.greenBrd}/>}/>
                {openTrades.length===0
                  ?<Empty icon="📭" title="No open positions" sub="Execute a signal to open a paper trade"/>
                  :<div>
                    {openTrades.map(t=>{
                      const pnl=t.pnl||0,pnlColor=pnl>0?C.green:pnl<0?C.red:C.muted;
                      const q=liveQuotes[t.symbol];
                      const totalMove=t.target1&&t.entry_price?Math.abs(t.target1-t.entry_price):1;
                      const curMove=Math.abs(pnl/Math.max(t.quantity,1));
                      const progress=Math.min(100,Math.max(0,(curMove/totalMove)*100));
                      return(
                        <div key={t.id} style={{padding:mobile?"14px":"16px",borderBottom:`1px solid ${C.border}`}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                            <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:6}}>
                              <span style={{fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:800,color:C.text}}>{t.symbol}</span>
                              <DirBadge d={t.direction}/>
                              <Pill label={t.mode} c={t.mode==="LIVE"?C.red:C.amber} bg={t.mode==="LIVE"?C.redBg:C.amberBg} bd={t.mode==="LIVE"?C.redBrd:C.amberBrd}/>
                            </div>
                            <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
                              <div style={{fontSize:mobile?18:20,fontWeight:800,color:pnlColor}}>{pnl>=0?"+":""}₹{Math.abs(pnl).toLocaleString("en-IN")}</div>
                              <div style={{fontSize:9,color:C.muted}}>Unrealised · {t.quantity} qty</div>
                              {q&&<div style={{fontSize:10,fontWeight:700,color:C.teal,marginTop:2}}>LTP ₹{q.ltp} <span style={{color:q.change_pct>=0?C.green:C.red}}>({q.change_pct>=0?"+":""}{q.change_pct?.toFixed(1)}%)</span></div>}
                            </div>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:12}}>
                            {[{l:"ENTRY",v:`₹${t.entry_price}`,c:C.amber,bg:C.amberBg,bd:C.amberBrd},
                              {l:"TARGET",v:`₹${t.target1}`,c:C.green,bg:C.greenBg,bd:C.greenBrd},
                              {l:"STOP LOSS",v:`₹${t.stop_loss}`,c:C.red,bg:C.redBg,bd:C.redBrd}].map(x=>(
                              <div key={x.l} style={{background:x.bg,borderRadius:7,padding:"8px",border:`1px solid ${x.bd}`}}>
                                <div style={{fontSize:8,color:x.c,marginBottom:2,letterSpacing:1,fontWeight:700,opacity:0.7}}>{x.l}</div>
                                <div style={{fontSize:13,fontWeight:700,color:x.c}}>{x.v}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{marginBottom:14}}>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:C.muted,marginBottom:4}}>
                              <span>Progress to Target</span><span>{Math.round(progress)}%</span>
                            </div>
                            <div style={{height:6,background:C.border,borderRadius:3,overflow:"hidden"}}>
                              <div style={{width:`${progress}%`,height:"100%",background:pnl>=0?C.green:C.red,borderRadius:3}}/>
                            </div>
                          </div>
                          <button className="btn" onClick={()=>handleExit(t.id)}
                            style={{width:"100%",padding:"14px",borderRadius:9,fontSize:13,fontWeight:700,fontFamily:"inherit",background:C.red,color:"#fff",border:"none",boxShadow:"0 3px 10px rgba(220,38,38,0.35)"}}>
                            🚪 Exit Trade — {t.symbol}
                          </button>
                        </div>
                      );
                    })}
                  </div>}
              </P>
            </div>}

            {/* HISTORY */}
            {activeTab==="history"&&<P>
              <PH title="TRADE HISTORY" right={<Pill label={`${history.length}`} c={C.muted} bg={C.panel2} bd={C.border}/>}/>
              {history.length===0?<Empty icon="📋" title="No completed trades" sub="Closed trades appear here"/>:
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:mobile?10:11}}>
                  <thead><tr style={{background:C.panel2,borderBottom:`1px solid ${C.border}`}}>
                    {(mobile?["Symbol","Dir","P&L","Status"]:["Entry Date","Exit Date","Symbol","Dir","Qty","Entry","Exit","P&L","Status"]).map(h=><th key={h} style={{padding:"9px 10px",textAlign:"left",color:C.muted,fontWeight:700,fontSize:9,whiteSpace:"nowrap"}}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {history.map(t=>(
                      <tr key={t.id} className="trow" style={{borderBottom:`1px solid ${C.border}`}}>
                        {!mobile&&<td style={{padding:"10px",color:C.muted,fontSize:9}}>{t.entered_at?.slice(0,10)||"—"}</td>}
                        {!mobile&&<td style={{padding:"10px",color:t.exited_at?C.text:C.dim,fontSize:9,fontWeight:t.exited_at?600:400}}>{t.exited_at?.slice(0,10)||"—"}</td>}
                        <td style={{padding:"10px",fontWeight:700,color:C.text}}>{t.symbol}</td>
                        <td style={{padding:"10px"}}><DirBadge d={t.direction}/></td>
                        {!mobile&&<td style={{padding:"10px",color:C.muted}}>{t.quantity}</td>}
                        {!mobile&&<td style={{padding:"10px",fontWeight:600}}>₹{t.entry_price}</td>}
                        {!mobile&&<td style={{padding:"10px",fontWeight:600}}>{t.exit_price?`₹${t.exit_price}`:"—"}</td>}
                        <td style={{padding:"10px",fontWeight:700,color:(t.pnl||0)>=0?C.green:C.red,whiteSpace:"nowrap"}}>{(t.pnl||0)>=0?"+":""}₹{Math.abs(t.pnl||0).toLocaleString("en-IN")}</td>
                        <td style={{padding:"10px"}}>
                          {t.status==="TARGET_HIT"&&<Pill label="✅ Target" c={C.green} bg={C.greenBg} bd={C.greenBrd}/>}
                          {t.status==="SL_HIT"&&<Pill label="❌ SL Hit" c={C.red} bg={C.redBg} bd={C.redBrd}/>}
                          {t.status==="MANUAL_EXIT"&&<Pill label="Manual" c={C.amber} bg={C.amberBg} bd={C.amberBrd}/>}
                          {!t.status&&<Pill label="Open" c={C.teal} bg={C.tealBg} bd={C.tealBrd}/>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}
            </P>}

            {/* ANALYTICS */}
            {activeTab==="analytics"&&(
              <AnalyticsTab API_URL={API} isMobile={mobile}/>
            )}

            {/* PORTFOLIO — mobile only tab */}
            {activeTab==="portfolio"&&(
              <div>
                <P style={{marginBottom:10}}>
                  <PH title="PORTFOLIO" right={<Pill label={`Win ${wr.toFixed(0)}%`} c={wr>=55?C.green:C.red} bg={wr>=55?C.greenBg:C.redBg} bd={wr>=55?C.greenBrd:C.redBrd}/>}/>
                  <div style={{padding:14}}>
                    {portfolio?(
                      <>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                          {[{l:"TOTAL P&L",v:`${(portfolio.total_pnl||0)>=0?"+":""}₹${Math.round(portfolio.total_pnl||0).toLocaleString("en-IN")}`,c:(portfolio.total_pnl||0)>=0?C.green:C.red},
                            {l:"CAPITAL",v:`₹${Math.round(portfolio.current_capital||0).toLocaleString("en-IN")}`,c:C.text},
                            {l:"PROFIT FACTOR",v:pf.toFixed(2),c:pf>=1.3?C.green:C.amber},
                            {l:"OPEN TRADES",v:portfolio.trades_open||0,c:C.teal},
                          ].map(k=>(
                            <div key={k.l} style={{background:C.panel2,borderRadius:9,padding:"11px 12px",border:`1px solid ${C.border}`}}>
                              <div style={{fontSize:8,color:C.muted,fontWeight:700,letterSpacing:1,marginBottom:3}}>{k.l}</div>
                              <div style={{fontSize:16,fontWeight:800,color:k.c}}>{k.v}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{marginBottom:12}}>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:10,fontWeight:700,marginBottom:6}}>
                            <span style={{color:C.green}}>✅ {portfolio.wins||0} Wins</span>
                            <span style={{color:C.red}}>❌ {portfolio.losses||0} Losses</span>
                          </div>
                          <div style={{height:7,background:C.redBg,borderRadius:4,overflow:"hidden"}}>
                            <div style={{width:`${wr}%`,height:"100%",background:C.green,borderRadius:4}}/>
                          </div>
                        </div>
                        <div style={{fontSize:9,color:C.muted,fontWeight:700,marginBottom:8}}>DAILY P&L</div>
                        {dailyPnL.length>0
                          ?<ResponsiveContainer width="100%" height={90}>
                              <BarChart data={dailyPnL} margin={{top:2,right:2,bottom:2,left:2}}>
                                <XAxis dataKey="date" tick={{fontSize:8,fill:C.dim}} axisLine={false} tickLine={false}/>
                                <YAxis tick={{fontSize:8,fill:C.dim}} axisLine={false} tickLine={false} tickFormatter={v=>`${v>=0?"+":""}${(v/1000).toFixed(0)}k`}/>
                                <Tooltip formatter={v=>[`${v>=0?"+":""}₹${Math.abs(v).toLocaleString("en-IN")}`,"P&L"]} contentStyle={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,fontSize:10}}/>
                                <ReferenceLine y={0} stroke={C.border2}/>
                                <Bar dataKey="pnl" radius={[4,4,0,0]} fill={C.green}/>
                              </BarChart>
                            </ResponsiveContainer>
                          :<div style={{height:70,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:C.dim,background:C.panel2,borderRadius:8}}>No P&L data yet</div>}
                        <div style={{marginTop:12}}>
                          {[{l:"Avg Win",v:`+₹${Math.round(portfolio.avg_win||0).toLocaleString("en-IN")}`,c:C.green},
                            {l:"Avg Loss",v:`-₹${Math.round(Math.abs(portfolio.avg_loss||0)).toLocaleString("en-IN")}`,c:C.red},
                            {l:"Total Trades",v:portfolio.trades_total||0,c:C.text},
                          ].map(r=>(
                            <div key={r.l} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
                              <span style={{color:C.muted}}>{r.l}</span>
                              <span style={{color:r.c,fontWeight:700}}>{r.v}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ):<Spin/>}
                  </div>
                </P>
                <P>
                  <PH title="LIVE READINESS" color={C.teal}/>
                  <div style={{padding:"8px 14px"}}>
                    {[{l:"Win Rate > 55%",ok:wr>=55},{l:"Profit Factor > 1.3",ok:pf>=1.3},{l:"Min 40 trades",ok:tot>=40},{l:"Drawdown < 15%",ok:true}].map(c=>(
                      <div key={c.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11,padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
                        <span style={{color:C.muted}}>{c.l}</span>
                        <span style={{fontWeight:700,color:c.ok?C.green:C.red}}>{c.ok?"✅":"❌"}</span>
                      </div>
                    ))}
                    <div style={{marginTop:10,padding:"9px",borderRadius:8,fontSize:10,fontWeight:700,textAlign:"center",background:C.amberBg,color:C.amber,border:`1px solid ${C.amberBrd}`}}>
                      ⏳ {Math.max(0,40-tot)} more paper trades needed
                    </div>
                  </div>
                </P>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL — desktop only */}
        {!mobile&&(
          <div style={{width:262,flexShrink:0,borderLeft:`1px solid ${C.border}`,overflowY:"auto",padding:12,background:C.bg}}>
            <P style={{marginBottom:10}}>
              <PH title="PORTFOLIO" right={<Pill label={`Win ${wr.toFixed(0)}%`} c={wr>=55?C.green:C.red} bg={wr>=55?C.greenBg:C.redBg} bd={wr>=55?C.greenBrd:C.redBrd}/>}/>
              <div style={{padding:12}}>
                {portfolio?(
                  <>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,marginBottom:10}}>
                      {[{l:"TOTAL P&L",v:`${(portfolio.total_pnl||0)>=0?"+":""}₹${Math.round(portfolio.total_pnl||0).toLocaleString("en-IN")}`,c:(portfolio.total_pnl||0)>=0?C.green:C.red},
                        {l:"CAPITAL",v:`₹${Math.round(portfolio.current_capital||0).toLocaleString("en-IN")}`,c:C.text},
                        {l:"PROFIT FACTOR",v:pf.toFixed(2),c:pf>=1.3?C.green:C.amber},
                        {l:"OPEN TRADES",v:portfolio.trades_open||0,c:C.teal},
                      ].map(k=>(
                        <div key={k.l} style={{background:C.panel2,borderRadius:8,padding:"9px 10px",border:`1px solid ${C.border}`}}>
                          <div style={{fontSize:7,color:C.muted,fontWeight:700,letterSpacing:1,marginBottom:3}}>{k.l}</div>
                          <div style={{fontSize:14,fontWeight:800,color:k.c}}>{k.v}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{marginBottom:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:9,fontWeight:700,marginBottom:5}}>
                        <span style={{color:C.green}}>✅ {portfolio.wins||0} Wins</span>
                        <span style={{color:C.red}}>❌ {portfolio.losses||0} Losses</span>
                      </div>
                      <div style={{height:6,background:C.redBg,borderRadius:3,overflow:"hidden"}}>
                        <div style={{width:`${wr}%`,height:"100%",background:C.green,borderRadius:3}}/>
                      </div>
                    </div>
                    <div style={{fontSize:8,color:C.muted,fontWeight:700,marginBottom:6}}>DAILY P&L</div>
                    {dailyPnL.length>0
                      ?<ResponsiveContainer width="100%" height={85}>
                          <BarChart data={dailyPnL} margin={{top:2,right:2,bottom:2,left:2}}>
                            <XAxis dataKey="date" tick={{fontSize:7,fill:C.dim}} axisLine={false} tickLine={false}/>
                            <YAxis tick={{fontSize:7,fill:C.dim}} axisLine={false} tickLine={false} tickFormatter={v=>`${v>=0?"+":""}${(v/1000).toFixed(0)}k`}/>
                            <Tooltip formatter={v=>[`${v>=0?"+":""}₹${Math.abs(v).toLocaleString("en-IN")}`,"P&L"]} contentStyle={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:7,fontSize:9}}/>
                            <ReferenceLine y={0} stroke={C.border2}/>
                            <Bar dataKey="pnl" radius={[3,3,0,0]} fill={C.green}/>
                          </BarChart>
                        </ResponsiveContainer>
                      :<div style={{height:60,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:C.dim,background:C.panel2,borderRadius:7}}>No P&L data yet</div>}
                  </>
                ):<Spin/>}
              </div>
            </P>
            {portfolio&&<P style={{marginBottom:10}}>
              <PH title="SESSION STATS"/>
              <div style={{padding:"7px 12px"}}>
                {[{l:"Avg Win",v:`+₹${Math.round(portfolio.avg_win||0).toLocaleString("en-IN")}`,c:C.green},
                  {l:"Avg Loss",v:`-₹${Math.round(Math.abs(portfolio.avg_loss||0)).toLocaleString("en-IN")}`,c:C.red},
                  {l:"Unrealised",v:`${(portfolio.unrealised_pnl||0)>=0?"+":""}₹${Math.round(Math.abs(portfolio.unrealised_pnl||0)).toLocaleString("en-IN")}`,c:C.amber},
                  {l:"Total Trades",v:portfolio.trades_total||0,c:C.text},
                ].map(r=>(
                  <div key={r.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:10,padding:"6px 0",borderBottom:`1px solid ${C.border}`}}>
                    <span style={{color:C.muted}}>{r.l}</span>
                    <span style={{color:r.c,fontWeight:700}}>{r.v}</span>
                  </div>
                ))}
              </div>
            </P>}
            <P>
              <PH title="LIVE READINESS" color={C.teal}/>
              <div style={{padding:"7px 12px"}}>
                {[{l:"Win Rate > 55%",ok:wr>=55},{l:"Profit Factor > 1.3",ok:pf>=1.3},{l:"Min 40 trades",ok:tot>=40},{l:"Drawdown < 15%",ok:true}].map(c=>(
                  <div key={c.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:10,padding:"6px 0",borderBottom:`1px solid ${C.border}`}}>
                    <span style={{color:C.muted}}>{c.l}</span>
                    <span style={{fontWeight:700,color:c.ok?C.green:C.red}}>{c.ok?"✅":"❌"}</span>
                  </div>
                ))}
                <div style={{marginTop:8,padding:"8px",borderRadius:7,fontSize:9,fontWeight:700,textAlign:"center",background:C.amberBg,color:C.amber,border:`1px solid ${C.amberBrd}`}}>
                  ⏳ {Math.max(0,40-tot)} more paper trades needed
                </div>
              </div>
            </P>
          </div>
        )}
      </div>

      {/* MOBILE BOTTOM NAV */}
      {mobile&&(
        <div style={{position:"fixed",bottom:0,left:0,right:0,background:C.panel,borderTop:`1px solid ${C.border}`,display:"flex",zIndex:100,boxShadow:"0 -2px 8px rgba(0,0,0,0.08)"}}>
          {tabs.map(t=>(
            <button key={t.k} className="btn" onClick={()=>setActiveTab(t.k)}
              style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",padding:"8px 4px",background:"transparent",color:activeTab===t.k?C.amber:C.muted,position:"relative"}}>
              <span style={{fontSize:18,lineHeight:1}}>{t.l}</span>
              <span style={{fontSize:8,fontWeight:700,marginTop:2}}>{t.label}</span>
              {t.n!=null&&t.n>0&&(
                <span style={{position:"absolute",top:4,right:"50%",transform:"translateX(12px)",background:C.red,color:"#fff",borderRadius:10,fontSize:8,fontWeight:700,padding:"1px 5px",minWidth:16,textAlign:"center"}}>{t.n}</span>
              )}
              {activeTab===t.k&&<div style={{position:"absolute",bottom:0,left:"20%",right:"20%",height:2,background:C.amber,borderRadius:2}}/>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}